import { Pencil, Plus, Trash2 } from 'lucide-react'
import { cn } from '@/lib/cn'
import { formatCurrency, pluralize } from '@/lib/format'
import type { AreaGroup } from '@/types/api'
import { Button } from '@/components/ui/Button'
import { Card, CardBody, CardHeader } from '@/components/ui/Card'
import { Badge } from '@/components/ui/StatusBadge'
import { toneClassForGroup } from './tones'
import styles from './AreaPricingPage.module.css'

interface GroupsPanelProps {
  groups: AreaGroup[]
  disabled: boolean
  onCreate: () => void
  onEdit: (group: AreaGroup) => void
  onDelete: (group: AreaGroup) => void
}

/** The groups, the primary one first, each with what an end of a trip in it adds. */
export function GroupsPanel({ groups, disabled, onCreate, onEdit, onDelete }: GroupsPanelProps) {
  return (
    <Card>
      <CardHeader
        title="Area groups"
        description="Each end of a Cairo trip pays its group's surcharge. The primary group adds nothing."
        actions={
          <Button size="sm" iconLeft={<Plus aria-hidden="true" />} onClick={onCreate} disabled={disabled}>
            Add group
          </Button>
        }
      />
      <CardBody>
        {groups.length === 0 ? (
          <p className={styles.empty}>
            No groups yet. Create the primary group first — Downtown — then one for each surcharge.
          </p>
        ) : (
          <ul className={styles.list}>
            {groups.map((group) => (
              <li key={group.id} className={styles.row}>
                <span className={cn(styles.swatch, toneClassForGroup(groups, group.id))} aria-hidden="true" />
                <div className={styles.rowMain} style={{ cursor: 'default' }}>
                  <span className={styles.rowName}>{group.name}</span>
                  <span className={styles.rowMeta}>
                    {group.primary ? (
                      <>
                        <Badge tone="success">Primary</Badge>{' '}
                      </>
                    ) : null}
                    {group.subAreaCount} {pluralize(group.subAreaCount, 'sub-area')}
                  </span>
                </div>
                <span className={styles.money}>
                  {group.primary || group.surchargeMinor === 0 ? 'No surcharge' : `+${formatCurrency(group.surcharge)}`}
                </span>
                <div className={styles.rowActions}>
                  <Button
                    size="sm"
                    variant="ghost"
                    iconLeft={<Pencil aria-hidden="true" />}
                    aria-label={`Edit ${group.name}`}
                    onClick={() => onEdit(group)}
                    disabled={disabled}
                  />
                  <Button
                    size="sm"
                    variant="ghost"
                    iconLeft={<Trash2 aria-hidden="true" />}
                    aria-label={`Delete ${group.name}`}
                    onClick={() => onDelete(group)}
                    disabled={disabled}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardBody>
    </Card>
  )
}
